import React, { useEffect, useRef, useState } from 'react';
import { IosShare } from '@mui/icons-material';
import { copyText } from '../utils/clipboard';

const FEEDBACK_MS = 1500;

// The className of the "pill" buttons in the StockTable explanation row (Subscribe and friends),
// so this sits with them. Those Tailwind classes are already in the checked-in prebuild because
// the row uses them; a class the prebuild lacks would silently do nothing.
const PILL_CLASS = 'inline-flex items-center justify-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium transition-all cursor-pointer bg-gray-100 text-gray-600 hover:bg-blue-100 hover:text-blue-700 shadow-sm';

// Inline, so the feedback state wins over the pill's hover classes.
const FEEDBACK_STYLE = {
  copied: { color: '#15803d', background: '#f0fdf4' },
  failed: { color: '#b91c1c', background: '#fef2f2' },
};

const LABELS = { copied: 'Link copied', failed: 'Copy failed' };

/**
 * One "Share" pill for a list. Click copies its absolute URL (origin + path) and the label
 * reads "Link copied" (or "Copy failed") for ~1.5s. The URL is the tooltip, so nothing else
 * needs printing.
 *
 * `path` comes from getListPath (utils/listUrl.js); the caller decides whether the list may
 * be shared at all, so a missing path renders nothing. `compact` tightens the padding, for the
 * /lists rows that already print the slug themselves.
 */
const ListShareButton = ({ path, compact = false }) => {
  // Keyed by path so a "Link copied" left over from the previous list can't show on the next one.
  const [feedback, setFeedback] = useState(null);
  const timerRef = useRef(null);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  if (!path) return null;

  const url = `${window.location.origin}${path}`;

  const handleCopy = async () => {
    const ok = await copyText(url);
    setFeedback({ path, ok });
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setFeedback(null), FEEDBACK_MS);
  };

  const current = feedback?.path === path ? feedback : null;
  const state = current ? (current.ok ? 'copied' : 'failed') : null;

  return (
    <button
      type="button"
      onClick={handleCopy}
      className={PILL_CLASS}
      title={url}
      // While feedback shows, the visible text is the accessible name so it gets announced.
      aria-label={state ? undefined : `Share: copy link to ${path}`}
      aria-live="polite"
      style={{
        ...(compact ? { padding: '1px 8px' } : null),
        ...(state ? FEEDBACK_STYLE[state] : null),
      }}
    >
      <IosShare style={{ fontSize: 14 }} />
      <span>{state ? LABELS[state] : 'Share'}</span>
    </button>
  );
};

export default ListShareButton;
